import type { ComponentType } from 'react';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { InitialArtifactParams } from '@cotera/sdk/core';
import type { NonFolderResourceType } from '../resources/type/resource-type-config';
import type { PendingChatSubmission } from '../chat/models/pending-chat-submission';

/** Shared URL query keys used by every chat seed. */
export const SHARED_CHAT_SEED_QUERY_KEYS = [
  'seed',
  'artifactId',
  'new',
  'folderId',
] as const;

export type ChatSeedBuildInput = {
  artifactId: string;
  isNew?: boolean;
  folderId?: string;
  /** Agent seed: override tool-key prefix when the saved key differs from the default. */
  agentKey?: string;
  /**
   * Workflow seed: an existing trigger to pre-bind onto the new workflow's graph
   * (e.g. "Create new workflow" clicked under a trigger card). Encoded into the
   * seed URL by the workflow strategy.
   */
  seedTrigger?: {
    id: string;
    kind: string;
    name: string;
    schedule: string | null;
    eventName?: string | null;
  };
};

export type ChatSeedPendingContext = Pick<
  PendingChatSubmission,
  | 'initialArtifacts'
  | 'initialToolKeys'
  | 'initialActions'
  | 'agentId'
  | 'displayTitle'
>;

export type ChatSeedNavigateFn = (
  path: string,
  options?: { state?: unknown }
) => void;

export type ChatSeedNavigateInput = ChatSeedBuildInput & {
  chatId?: string;
  replace?: boolean;
};

export type ChatSeedNavigateResult = {
  chatId: string;
  artifactId: string;
};

/** Props passed to seed chrome components rendered around the chat input. */
export type ChatSeedChromeProps = ChatSeedBuildInput;

export type ChatSeedChrome = {
  Heading?: ComponentType<ChatSeedChromeProps>;
  Footer?: ComponentType<ChatSeedChromeProps>;
};

export type ChatSeedSubmitParams = Pick<
  PendingChatSubmission,
  'initialArtifacts' | 'initialActions' | 'initialToolKeys' | 'agentId'
>;

export type ChatSeedPreSubmitClients = {
  agent: TenantedClient['resource']['agent'];
  workflow: TenantedClient['resource']['workflow'];
  dataset: TenantedClient['resource']['dataset'];
};

export type ChatSeedPreSubmitContext = {
  params: ChatSeedSubmitParams;
  folderId?: string;
  clients: ChatSeedPreSubmitClients;
};

export type ChatSeedPreSubmitResult =
  | { ok: true }
  | { ok: false; message: string };

function artifactDataIsNew(data: InitialArtifactParams['data']): boolean {
  if (data === null || data === undefined || typeof data !== 'object') {
    return false;
  }
  return (data as { isNew?: boolean }).isNew === true;
}

/**
 * Drops the client-only `isNew` marker from seeded artifact data before it is
 * persisted onto the conversation. It means "this resource does not exist on the
 * server yet", which stops being true as soon as pre-submit creates it. Persisted
 * it would outlive the seed: every later open of the chat would render the
 * artifact as a brand-new blank draft instead of the saved resource.
 */
export function withoutSeedIsNewFlag<T extends { data?: unknown }>(
  artifacts: T[] | undefined
): T[] | undefined {
  return artifacts?.map((artifact) => {
    if (!artifactDataIsNew(artifact.data)) {
      return artifact;
    }
    const data = { ...(artifact.data as Record<string, unknown>) };
    delete data['isNew'];
    return { ...artifact, data };
  });
}

/** True when the seeded artifact for this strategy is marked `isNew`. */
export function seedArtifactIsNew(
  params: ChatSeedSubmitParams,
  artifactType: string,
  artifactId: string
): boolean {
  return (
    params.initialArtifacts?.some(
      (artifact) =>
        artifact.artifactType === artifactType &&
        artifact.artifactId === artifactId &&
        artifactDataIsNew(artifact.data)
    ) ?? false
  );
}

/**
 * Per-resource chat seed: owns URL encoding, client title, and pending submission shape.
 * Register new strategies in {@link chatSeedRegistry}.
 */
export type ChatSeedStrategy = {
  readonly type: string;
  /** Cotera resource type for the seeded artifact (used for folder inheritance). */
  readonly artifactResourceType: NonFolderResourceType;
  /** Extra URL keys to strip on persist (beyond {@link SHARED_CHAT_SEED_QUERY_KEYS}). */
  readonly extraStripQueryKeys?: readonly string[];
  buildUrlSearchParams: (input: ChatSeedBuildInput) => URLSearchParams;
  parseFromUrl: (searchParams: URLSearchParams) => PendingChatSubmission | null;
  /**
   * Build deferred submit context (e.g. agent page → new chat without persisting).
   * When `isNew` is true, strategies typically include a client-only `displayTitle`.
   */
  buildPendingContext: (input: ChatSeedBuildInput) => ChatSeedPendingContext;
  /** Optional heading/footer shown around the chat input while the chat is seeded. */
  getChatChrome?: () => ChatSeedChrome;
  /**
   * Called before the first chat message is submitted for a seeded conversation.
   * Return `null` when this strategy does not apply to the submission.
   */
  preSubmit?: (
    ctx: ChatSeedPreSubmitContext
  ) => Promise<ChatSeedPreSubmitResult | null>;
};
